import type { ConvexReactClient } from 'convex/react';
import type { NavigationDraftCheckpoint } from './navigation-draft-checkpoint';
import type { Id } from '@/convex/_generated/dataModel';
import { api } from '@/lib/convex';
import type {
  OrgChatAttachmentInput,
  OrgChatPresetDataInput,
} from '@/components/org-chat/org-chat-types';
import type { OrgChatSendOptions } from '@/contexts/org-chat-interface-context';
import { dispatchThreadTargets } from '@/components/org-chat/org-chat-delivery';
import { prepareSharedDispatchUploads } from '@/components/org-chat/org-chat-shared-dispatch-uploads';
import { buildBaseMessagePayload } from '@/components/org-chat/payload/build-base-payload';
import {
  buildAttachmentFields,
  buildPresetFields,
  mergeReferencePayloadFields,
  type OrgChatSendPayload,
} from '@/components/org-chat/org-chat-payload-builders';
import {
  designClosedArtifactPanelState,
  normalizeDesignTargetThreadRouteRefs,
} from './shared';

export type DesignNavigationDelivery = {
  id: string;
  actorId: string;
  orgSlug: string;
  routeRef: string;
  orgId: Id<'organizations'>;
  title: string;
  visibility: 'organization' | 'private';
  projectId?: Id<'threadFolders'>;
  prompt: string;
  rawUserPrompt?: string;
  attachments?: OrgChatAttachmentInput[];
  presetData?: OrgChatPresetDataInput;
  options: OrgChatSendOptions;
  sharedFields?: Partial<OrgChatSendPayload>;
  sourceDraftCheckpoint?: NavigationDraftCheckpoint;
};

/** The unbound snapshot survives navigation; each attempt binds each destination once. */
export async function deliverDesignNavigation(
  job: DesignNavigationDelivery,
  client: Pick<ConvexReactClient, 'query' | 'mutation'>,
  checkpoint: (job: DesignNavigationDelivery) => void,
  assertActor: () => void = () => {}
) {
  assertActor();
  // Older persisted navigation jobs may predate the input timestamp.
  if (job.options.clientSendStartedAt === undefined) {
    job.options = { ...job.options, clientSendStartedAt: Date.now() };
    checkpoint(job);
  }
  const clientSendStartedAt = job.options.clientSendStartedAt;
  const attachmentFields = buildAttachmentFields(job.attachments);
  if (!job.sharedFields) {
    job.sharedFields = await prepareSharedDispatchUploads(
      { ...attachmentFields, ...buildPresetFields(job.presetData) },
      (uploads) => {
        assertActor();
        return client.mutation(
          api.generations.mutations.upsertComposerUploadedFilesForCurrentUser,
          { orgSlug: job.orgSlug, uploads }
        );
      }
    );
    checkpoint(job);
  }
  return dispatchThreadTargets({
    existingRouteRefs: normalizeDesignTargetThreadRouteRefs(job.options),
    newRouteRefs: job.options.newThreadRouteRefs ?? [],
    options: job.options,
    resolve: async (routeRefs) => {
      assertActor();
      if (!routeRefs.length) return [];
      const entries: Array<{
        routeRef: string;
        thread: { _id: Id<'orgThreads'> } | null;
      }> = await client.query(
        api.organizations.threads.queries.getManyByRouteRefsForCurrentUser,
        { routeRefs }
      );
      return entries.flatMap((entry) =>
        entry.thread
          ? [{ routeRef: entry.routeRef, threadId: entry.thread._id }]
          : []
      );
    },
    createAndSend: async (routeId) => {
      assertActor();
      const receipt = await client.mutation(
        api.organizations.threads.create_and_send.createThreadAndSendMessage,
        {
          ...buildMessage(routeId),
          routeId,
          title: job.title,
          visibility: job.visibility,
          ...(job.projectId ? { projectId: job.projectId } : {}),
        }
      );
      return receipt.result;
    },
    send: (orgThreadId, routeRef) => {
      assertActor();
      return client.mutation(
        api.products.ai.mutations.org_thread_send_message.sendMessage,
        { ...buildMessage(routeRef), orgThreadId }
      );
    },
  });

  function buildMessage(routeRef: string) {
    return mergeReferencePayloadFields(
      {
        ...buildBaseMessagePayload({
          orgId: job.orgId,
          prompt: job.prompt,
          rawUserPrompt: job.rawUserPrompt,
          forceDeepResearch: job.options.forceDeepResearch,
        }),
        ...job.sharedFields,
        artifactPanelState: designClosedArtifactPanelState(),
        deliveryMetadata: {
          deliveryKind: 'send' as const,
          clientInputId: `${job.id}:${routeRef}`,
          createdAt: clientSendStartedAt,
        },
      },
      attachmentFields
    );
  }
}
